import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, Sun, Sparkles } from 'lucide-react';

export default function GuestLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E7E5E4] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <span className="font-serif tracking-wide whitespace-nowrap text-2xl text-[#1C1917]" data-testid="header-logo">Acqualina Resort</span>
            <h1 className="text-2xl md:text-3xl font-serif text-[#1C1917] hidden md:block" data-testid="header-title">
              Reserve Your Perfect Escape
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/manage')}
              className="text-[#9F1239] font-serif tracking-wide hover:underline underline-offset-4"
              data-testid="header-manage-link"
            >
              Manage my reservation
            </button>
            <Button
              onClick={() => navigate('/check-eligibility')}
              className="bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-8 py-3 font-serif tracking-wide transition-all duration-300 shadow-md hover:shadow-lg"
              data-testid="header-reserve-btn"
            >
              Reserve Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-screen grain-overlay">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4)), url('https://customer-assets.emergentagent.com/job_reserve-aquacana/artifacts/h4vzps6z_Acqualina_pool.png')`
          }}
        />
        
        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <div className="max-w-4xl text-center">
            <p className="text-xl md:text-2xl text-white/95 font-sans leading-relaxed max-w-2xl mx-auto drop-shadow-xl" data-testid="hero-subtitle" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
              Experience luxury poolside and beachfront seating at the finest resort destinations.
              Reserve premium cabanas, daybeds, and lounge chairs with ease.
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-white border-y border-[#E7E5E4]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl md:text-5xl font-serif tracking-tight text-[#1C1917] mb-6" data-testid="cta-title">
            Ready to Reserve?
          </h3>
          <p className="text-lg text-[#78716C] leading-relaxed mb-10">
            Check your eligibility and browse available seating in seconds.
          </p>
          <Button
            onClick={() => navigate('/check-eligibility')}
            className="bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-12 py-7 text-lg font-serif tracking-wide transition-all duration-300 shadow-md hover:shadow-xl"
            data-testid="cta-reserve-btn"
          >
            Get Started
          </Button>
          <p className="mt-6 text-[#78716C]">
            Already booked?{' '}
            <button onClick={() => navigate('/manage')} className="text-[#9F1239] font-medium hover:underline underline-offset-4" data-testid="cta-manage-link">
              Manage my reservation
            </button>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#1C1917] text-[#FAFAF9]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <span className="font-serif tracking-wide whitespace-nowrap text-2xl text-[#FAFAF9]" data-testid="brand-name">Acqualina Resort</span>
          </div>
          <button onClick={() => navigate('/help')} className="text-sm text-[#FAFAF9] hover:text-white underline-offset-4 hover:underline mb-4" data-testid="footer-help-link">
            Help Center
          </button>
          <p className="text-sm text-[#A8A29E]">© 2026 Acqualina Resort & Residences. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
